import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import { ChevronDownIcon, MoreHorizontalIcon } from '#/shared/ui/icons'
import { Popover } from '#/shared/ui/popover'

import { ArchiveReasonsPanel } from './archive-reason-dropdown'

export const MORE_MENU_ACTION = {
  activityHistory: 'activityHistory',
  duplicate: 'duplicate',
} as const

export type MoreMenuAction =
  (typeof MORE_MENU_ACTION)[keyof typeof MORE_MENU_ACTION]

interface MenuItem {
  value: MoreMenuAction
  label: string
}

export interface MarkAsOption {
  value: string
  label: string
}

export interface MarkAsConfig {
  label: string
  currentStatus?: string
  options: MarkAsOption[]
  onSelect: (status: string) => void
}

export interface ArchiveSubmenuConfig {
  label: string
  stage?: string
  shipmentId?: string
  onArchived?: () => void
}

/** Context-dependent entry (e.g. stage-specific copy-link actions) appended
 *  after the fixed menu items. */
export interface ExtraMenuItem {
  key: string
  label: string
  onSelect: () => void
}

export interface MoreMenuDropdownProps {
  label: string
  onSelect?: (action: MoreMenuAction) => void
  extraItems?: ExtraMenuItem[]
  markAs?: MarkAsConfig
  /** "Back to Quotes" status submenu — same shape as `markAs`; the picked
   *  status is the quote status the shipment lands on. */
  backToQuote?: MarkAsConfig
  archive?: ArchiveSubmenuConfig
  /** Show the archive submenu at every breakpoint. The full toolbar keeps it
   *  mobile-only (it has a dedicated desktop archive button); the compact
   *  toolbar — mini detail view — has no such button, so it shows it always. */
  archiveAlways?: boolean
  triggerClassName?: string
}

const SUBMENU_GAP_PX = 2
const PANEL_PADDING_PX = 4

function useAnchorRect(
  open: boolean,
  ref: React.RefObject<HTMLElement | null>,
) {
  const [rect, setRect] = useState<DOMRect | null>(null)
  useLayoutEffect(() => {
    if (!open) {
      setRect(null)
      return
    }
    const update = () => {
      const el = ref.current
      if (el) setRect(el.getBoundingClientRect())
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [open, ref])
  return rect
}

function MarkAsSubmenu({
  config,
  onPick,
}: {
  config: MarkAsConfig
  onPick: () => void
}) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const rect = useAnchorRect(open, triggerRef)

  useEffect(() => {
    if (!open) return
    const onDocClick = (event: MouseEvent) => {
      const target = event.target as Node
      if (triggerRef.current?.contains(target)) return
      if (panelRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  const visibleOptions = config.options.filter(
    (option) => option.value !== config.currentStatus,
  )

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'flex h-8 w-full cursor-pointer select-none items-center justify-between rounded-sm px-2.5 text-left text-sm text-gray-900 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none',
          open && 'bg-gray-100',
        )}
      >
        <span>{config.label}</span>
        <ChevronDownIcon
          className={cn(
            'size-3 text-gray-500 transition-transform duration-150',
            open && 'rotate-180',
          )}
        />
      </button>
      {open &&
        rect &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: 'fixed',
              top: rect.bottom + SUBMENU_GAP_PX,
              left: rect.right + PANEL_PADDING_PX,
              transform: 'translateX(-100%)',
              minWidth: rect.width + PANEL_PADDING_PX * 2,
              zIndex: 80,
            }}
            className="flex w-44 flex-col gap-0.5 rounded-md border border-gray-200 bg-white p-1 shadow-popover"
          >
            {visibleOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  config.onSelect(option.value)
                  setOpen(false)
                  onPick()
                }}
                className="flex h-8 w-full cursor-pointer select-none items-center rounded-sm px-2.5 text-left text-sm text-gray-900 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
              >
                {option.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  )
}

function ArchiveSubmenu({
  config,
  onPick,
}: {
  config: ArchiveSubmenuConfig
  onPick: () => void
}) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const rect = useAnchorRect(open, triggerRef)

  useEffect(() => {
    if (!open) return
    const onDocClick = (event: MouseEvent) => {
      const target = event.target as Node
      if (triggerRef.current?.contains(target)) return
      if (panelRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'flex h-8 w-full cursor-pointer select-none items-center justify-between rounded-sm px-2.5 text-left text-sm text-gray-900 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none',
          open && 'bg-gray-100',
        )}
      >
        <span>{config.label}</span>
        <ChevronDownIcon
          className={cn(
            'size-3 text-gray-500 transition-transform duration-150',
            open && 'rotate-180',
          )}
        />
      </button>
      {open &&
        rect &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: 'fixed',
              top: rect.bottom + SUBMENU_GAP_PX,
              left: rect.right + PANEL_PADDING_PX,
              transform: 'translateX(-100%)',
              zIndex: 80,
            }}
          >
            <ArchiveReasonsPanel
              open={open}
              stage={config.stage}
              shipmentId={config.shipmentId}
              onArchived={() => {
                config.onArchived?.()
                setOpen(false)
                onPick()
              }}
              onClose={() => {
                setOpen(false)
                onPick()
              }}
            />
          </div>,
          document.body,
        )}
    </>
  )
}

function Panel({
  items,
  extraItems,
  markAs,
  backToQuote,
  archive,
  archiveAlways,
  onSelect,
  onClose,
}: {
  items: MenuItem[]
  extraItems?: ExtraMenuItem[]
  markAs?: MarkAsConfig
  backToQuote?: MarkAsConfig
  archive?: ArchiveSubmenuConfig
  archiveAlways?: boolean
  onSelect?: (action: MoreMenuAction) => void
  onClose: () => void
}) {
  return (
    <div className="flex w-44 flex-col gap-0.5 rounded-md bg-white p-1 shadow-popover">
      {items.map((item) => (
        <button
          key={item.value}
          type="button"
          onClick={() => {
            onSelect?.(item.value)
            onClose()
          }}
          className="flex h-8 w-full cursor-pointer select-none items-center rounded-sm px-2.5 text-left text-sm text-gray-900 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
        >
          {item.label}
        </button>
      ))}
      {extraItems?.map((item) => (
        <button
          key={item.key}
          type="button"
          onClick={() => {
            item.onSelect()
            onClose()
          }}
          className="flex h-8 w-full cursor-pointer select-none items-center rounded-sm px-2.5 text-left text-sm text-gray-900 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
        >
          {item.label}
        </button>
      ))}
      {backToQuote && <MarkAsSubmenu config={backToQuote} onPick={onClose} />}
      {markAs && <MarkAsSubmenu config={markAs} onPick={onClose} />}
      {archive &&
        (archiveAlways ? (
          <ArchiveSubmenu config={archive} onPick={onClose} />
        ) : (
          <div className="md:hidden">
            <ArchiveSubmenu config={archive} onPick={onClose} />
          </div>
        ))}
    </div>
  )
}

export function MoreMenuDropdown({
  label,
  onSelect,
  extraItems,
  markAs,
  backToQuote,
  archive,
  archiveAlways,
  triggerClassName,
}: MoreMenuDropdownProps) {
  const { t } = useTranslation()
  const items: MenuItem[] = [
    {
      value: MORE_MENU_ACTION.activityHistory,
      label: t('detailToolbar.moreOptions.activityHistory'),
    },
    {
      value: MORE_MENU_ACTION.duplicate,
      label: t('detailToolbar.moreOptions.duplicate'),
    },
  ]
  return (
    <div className="w-fit">
      <Popover
        align="right"
        trigger={(open) => (
          <button
            type="button"
            aria-label={label}
            className={cn(
              'flex h-8 cursor-pointer items-center justify-center rounded-md border border-gray-200 bg-white px-2.5 text-gray-500 transform-gpu transition-[transform,background-color,color,border-color] duration-150 ease-out hover:bg-gray-50 hover:text-gray-900 active:scale-[0.98] focus-visible:outline-none focus-visible:focus-ring',
              triggerClassName,
              open && 'focus-ring',
            )}
          >
            <MoreHorizontalIcon className="size-5" />
          </button>
        )}
      >
        {(close) => (
          <Panel
            items={items}
            extraItems={extraItems}
            markAs={markAs}
            backToQuote={backToQuote}
            archive={archive}
            archiveAlways={archiveAlways}
            onSelect={onSelect}
            onClose={close}
          />
        )}
      </Popover>
    </div>
  )
}
